import React, {useContext, useState} from "react";
import {Box, ResponsiveContext, Text} from "grommet";
import {formatCurrency} from "../../../utils/numbers";
import {formatDateUTC} from "../../../utils/dates";
import {sentencecase, titlecase, unsnakecase} from "../../../utils/string";
import {csvFilename, getCSVConfig} from "../../../utils/csv";
import DataTableWithBackendSearch from "../../DataTableWithBackendSearch";
import {getQueryString} from "../../../utils/search";
import {useRouter} from "next/router";
import {downloadBlobAsCsvFile} from "../../../lib/exportcsv";
import {handleFileDownloadErrors} from "../../../utils/errors";
import {NotificationsContext} from "../../NotificationsProvider";
import {useSession} from "next-auth/react";

const PayoutList = ({ payouts, title }) => {
    const router = useRouter();
    const notificationCtx = useContext(NotificationsContext);
    const [csvQueryUrl, setCsvQueryUrl] = useState();
    const {data: session} = useSession();
    const {company} = session || {};
    
    const size = useContext(ResponsiveContext);
    const isMobile = size === "small";
    const columns = isMobile ? [
        {
            selector: row => row.id,
            cell: (datum) => {
                return (
                    <Box data-tag="allowRowEvents" fill="horizontal" justify="between" direction="row" gap="small" align="center" pad={{right: 'xsmall'}}>
                        <Box data-tag="allowRowEvents">
                            <Text data-tag="allowRowEvents">{datum.formatted_payout_date}</Text>
                            <Text data-tag="allowRowEvents" size="small" color="text-weak">{sentencecase(datum?.settlement_type)}</Text>
                        </Box>
                        <Box data-tag="allowRowEvents">
                            <Text data-tag="allowRowEvents" wordBreak="keep-all">{formatCurrency(datum?.total, datum?.currency)}</Text>
                        </Box>
                    </Box>
                )
            }
        }
    ]:[
        {
            selector: row => row.payout_date,
            name: 'Payout Date',
            sortable: true,
            grow: 1.5,
            sortField: 'payout_date',
            cell: (datum) => datum.formatted_payout_date,
        },
        {
            selector: row => row.settlement_type,
            name: 'Type',
            cell: (datum) => titlecase(unsnakecase(datum.settlement_type)) || ''
        },
        {
            selector: row => row.total,
            name: 'Gross amount',
            right: true,
            sortable: true,
            type: 'currency',
            sortField: 'total',
            cell: (datum) => formatCurrency(datum.total, datum.currency)
        },
        {
            selector: row => row.disbursement_fee_amount,
            name: 'Fees',
            right: true,
            sortable: true,
            type: 'currency',
            sortField: 'disbursement_fee_amount',
            cell: (datum) => formatCurrency(datum.disbursement_fee_amount, datum.currency)
        },
        {
            selector: row => row.disbursement_amount,
            name: 'Net amount',
            right: true,
            sortable: true,
            type: 'currency',
            sortField: 'disbursement_amount',
            cell: (datum) => formatCurrency(datum.disbursement_amount, datum.currency)
        }
    ];

    const getPayoutsReportCSV = ({callback, url}) => {
        const exportError = {type: 'error', message: 'Unable to download csv data. Try again later.'};
        fetch(url, {
            method: 'GET'
        })
            .then(handleFileDownloadErrors)
            .then((data) => {
                data ?
                    downloadBlobAsCsvFile(
                        data,
                        csvFilename('payout_report')
                    ) :
                    notificationCtx.showNotification(exportError);
                callback && callback();
            })
            .catch(e => notificationCtx.showNotification(exportError));
    };

    return (
        <Box>
            {payouts && (
                <>
                    <DataTableWithBackendSearch
                        noTableHead={isMobile}
                        emptyListMessage={`No ${title ? title.toLowerCase() : 'data'} available`}
                        title={title}
                        reshape={(data) => {
                            if (!data) return null;
                            return data.payouts.map((payout, index) => ({
                                ...payout,
                                formatted_payout_date: formatDateUTC(payout.payout_date),
                                payout_date: new Date(payout.payout_date),
                                rowIndex: index // id is not unique in each payout record. Need a new key field
                            }));
                        }}
                        setQueryUrlCallback={(url) => { // get query url from since data table controls the state of filters and sorting
                            const queryParams = url.split("?")[1]; // get only the url
                            setCsvQueryUrl(`/api/reports/payouts/csv?${queryParams || ''}`);
                        }}
                        getUrl={props => (
                            getQueryString({
                                ...props,
                                baseUrl: 'api/reports/payouts'
                            })
                        )}
                        onExportCSVClicked={() => getPayoutsReportCSV({url: csvQueryUrl})}
                        columns={columns}
                        exportConfigs={getCSVConfig(columns)}
                        onRowClicked={(row) => router.push(`/reports?payout_id=${row.id}`, undefined, { scroll: false })}
                        isServer
                        keyField="rowIndex"
                        hideSearch
                        dataName="payouts"
                    />
                </>
            )}
        </Box>
    );
};

export default PayoutList;
